import { useState } from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material'
import {
  Menu as MenuIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Language as LanguageIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon
} from '@mui/icons-material'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { 
  toggleSidebar, 
  toggleTheme, 
  setLanguage,
  selectThemeMode,
  selectLanguage
} from '@store/slices/uiSlice'
import { logout } from '@store/slices/authSlice'

const Topbar = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const themeMode = useSelector(selectThemeMode)
  const language = useSelector(selectLanguage)
  
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  
  const [anchorEl, setAnchorEl] = useState(null)
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null)

                // Function to get current page title
              const getCurrentPageTitle = () => {
                const path = location.pathname
                if (path === '/dashboard') return t('navigation.dashboard')
                if (path === '/users') return t('navigation.users')
                if (path === '/reminders') return t('navigation.reminders')
                if (path === '/subscriptions') return t('navigation.subscriptions')
                if (path === '/profile') return t('navigation.profile')
                return t('navigation.dashboard')
              }

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLanguageMenuOpen = (event) => {
    setLanguageAnchorEl(event.currentTarget)
  }

  const handleLanguageMenuClose = () => {
    setLanguageAnchorEl(null)
  }

  const handleThemeToggle = () => {
    dispatch(toggleTheme())
  }

  const handleLanguageChange = (lang) => {
    dispatch(setLanguage(lang))
    i18n.changeLanguage(lang)
    handleLanguageMenuClose()
  }

  const handleLogout = () => {
    dispatch(logout())
    navigate('/login')
    handleProfileMenuClose()
  }

  const handleProfile = () => {
    navigate('/profile')
    handleProfileMenuClose()
  }



  const isProfileMenuOpen = Boolean(anchorEl)
  const isLanguageMenuOpen = Boolean(languageAnchorEl)

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: 'text.primary'
      }}
    >
                        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
                    {/* Mobile Menu Button */}
                    {isMobile && (
                      <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        onClick={() => dispatch(toggleSidebar())}
                        sx={{ mr: { xs: 1, sm: 2 } }}
                      >
                        <MenuIcon />
                      </IconButton>
                    )}

                    {/* Title */}
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{ 
                        flexGrow: 1, 
                        display: { xs: 'block', sm: 'block' },
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                      }}
                    >
                      {getCurrentPageTitle()}
                    </Typography>

                            {/* Right Side Actions */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 0.5, sm: 1 },
                      ml: 'auto',
                      mr: { xs: 0.5, sm: 1 }
                    }}>
          {/* Theme Toggle */}
                                <Tooltip title={t(`themes.${themeMode === 'dark' ? 'light' : 'dark'}`)}>
                        <IconButton
                          color="inherit"
                          onClick={handleThemeToggle}
                          size="small"
                          sx={{ 
                            color: 'text.secondary',
                            '&:hover': { backgroundColor: 'action.hover' },
                            p: { xs: 0.5, sm: 1 }
                          }}
                        >
                          {themeMode === 'dark' ? <LightIcon /> : <DarkIcon />}
                        </IconButton>
                      </Tooltip>

          {/* Language Selector */}
          <Tooltip title={t('common.language')}>
            <IconButton
              color="inherit"
              onClick={handleLanguageMenuOpen}
              sx={{ 
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <LanguageIcon />
            </IconButton>
          </Tooltip>



          {/* User Profile */}
          <Tooltip title={t('common.profile')}>
            <IconButton
              edge="end"
              onClick={handleProfileMenuOpen}
              sx={{ 
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Avatar
                sx={{ 
                  width: 32, 
                  height: 32,
                  backgroundColor: 'primary.main',
                  fontSize: '0.875rem'
                }}
              >
                A
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={isProfileMenuOpen}
          onClose={handleProfileMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleProfile}>
            <AccountIcon sx={{ mr: 2 }} />
            {t('navigation.profile')}
          </MenuItem>
          
          <MenuItem onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 2 }} />
            {t('navigation.logout')}
          </MenuItem>
        </Menu>

        {/* Language Menu */}
        <Menu
          anchorEl={languageAnchorEl}
          open={isLanguageMenuOpen}
          onClose={handleLanguageMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => handleLanguageChange('en')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>🇺🇸</span>
              {t('languages.en')}
              {language === 'en' && <Box component="span" sx={{ color: 'primary.main' }}>✓</Box>}
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleLanguageChange('ar')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>🇸🇦</span>
              {t('languages.ar')}
              {language === 'ar' && <Box component="span" sx={{ color: 'primary.main' }}>✓</Box>}
            </Box>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  )
}

export default Topbar
